import { dataPayload } from "./utils";
import * as api from "../api/adapters/json_api";
import isEmpty from "lodash/isEmpty";

const TYPE = "request_for_quotations";
const PATH = `api_resources/${TYPE}`;

/**
 * Returns the sort options if they are not empty.
 * @param {Object} sort - The sort settings.
 * @param {string} sort.field - The field to sort by.
 * @param {string} sort.direction - The direction of sorting ('asc' or 'desc').
 * @returns {string} - The sort options.
 */
const sortOptions = (sort) => {
  if (isEmpty(sort.field)) {
    return "-created_at";
  }
  const directionPrefix = sort.direction === "asc" ? "" : "-";
  return `${directionPrefix}${sort.field}`;
};

/**
 * @typedef {Object} RequestForQuotations
 * @property {function(Object): Promise<Object>} index
 * @property {function(string, string): Promise<Object>} addRequisitionList
 * @property {function(string, string): Promise<Object>} removeRequisitionList
 * @property {function(string, string): Promise<Object>} addSupplier
 * @property {function(string, string): Promise<Object>} removeSupplier
 * @property {function(string): Promise<Object>} removeAllSuppliers
 * @property {function(string): Promise<Object>} show
 * @property {function(Object): Promise<Object>} create
 * @property {function(string, Object): Promise<Object>} update
 */

/**
 * @type {RequestForQuotations}
 */
const requestForQuotations = {
  /**
   * Fetch list of RFQs with pagination, sorting and filtering
   * @param {Object} options - Query options
   * @param {Object} options.pagination - Pagination settings
   * @param {Object} options.sort - Sort settings
   * @param {Object} options.filter - Filter settings
   * @returns {Promise<Object>} The API response
   */
  index: (options = {}) => {
    const { pagination = {}, sort = {}, filter = {} } = options;
    return api.read(
      { type: PATH },
      {
        query: {
          sort: sortOptions(sort),
          page: {
            size: pagination.itemsPerPage || 20,
            number: pagination.activePage || 1,
          },
          filter: {
            ...filter,
          },
        },
      }
    );
  },

  /**
   * Add a requisition list to a request for quotation
   * @param {string} rfqId - The ID of the request for quotation
   * @param {string} requisitionListId - The ID of the requisition list to add
   * @returns {Promise<Object>} The API response
   */
  addRequisitionList: (rfqId, requisitionListId) => api.create(
    { type: PATH, id: rfqId, action: "add_requisition_list" },
    {},
    { requisition_list_id: requisitionListId }
  ),

  /**
   * Remove a requisition list from a request for quotation
   * @param {string} rfqId - The ID of the request for quotation
   * @param {string} requisitionListId - The ID of the requisition list to remove
   * @returns {Promise<Object>} The API response
   */
  removeRequisitionList: (rfqId, requisitionListId) => api.create(
    { type: PATH, id: rfqId, action: "remove_requisition_list" },
    {},
    { requisition_list_id: requisitionListId }
  ),

  /**
   * Add a supplier to a request for quotation
   * @param {string} rfqId - The ID of the request for quotation
   * @param {string} supplierId - The ID of the supplier to add
   * @returns {Promise<Object>} The API response
   */
  addSupplier: (rfqId, supplierId) => api.create(
    { type: PATH, id: rfqId, action: "add_supplier" },
    {},
    { supplier_id: supplierId }
  ),

  /**
   * Remove a supplier from a request for quotation
   * @param {string} rfqId - The ID of the request for quotation
   * @param {string} supplierId - The ID of the supplier to remove
   * @returns {Promise<Object>} The API response
   */
  removeSupplier: (rfqId, supplierId) => api.create(
    { type: PATH, id: rfqId, action: "remove_supplier" },
    {},
    { supplier_id: supplierId }
  ),

  /**
   * Remove all suppliers from a request for quotation
   * @param {string} rfqId - The ID of the request for quotation
   * @returns {Promise<Object>} The API response
   */
  removeAllSuppliers: (rfqId) => api.create(
    { type: PATH, id: rfqId, action: "remove_all_suppliers" },
    {},
    {}
  ),

  /**
   * Fetch a request for quotation by ID
   * @param {string} id - The ID of the request for quotation
   * @returns {Promise<Object>} The API response
   */
  show: async (id) => api.read({ type: PATH, id }, {}),

  /**
   * Create a new request for quotation
   * @param {Object} attributes - The attributes of the request for quotation
   * @param {string[]} [attributes.rlIds=[]] - The IDs of requisition lists to add
   * @returns {Promise<Object>} The API response
   */
  create: async (attributes) => {
    const { rlIds = [], ...restAttributes } = attributes;
    const payload = dataPayload(TYPE, restAttributes);

    if (rlIds.length) {
      const reqResp = await api.create({ type: PATH }, {}, payload);
      return requestForQuotations.addRequisitionList(reqResp.data.data.id, rlIds);
    }

    return api.create({ type: PATH, action: "create" }, {}, payload);
  },

  /**
   * Update an existing request for quotation
   * @param {string} id - The ID of the request for quotation to update
   * @param {Object} attributes - The attributes to update
   * @returns {Promise<Object>} The API response
   */
  update: async (id, attributes) => {
    const payload = dataPayload(TYPE, attributes, id);
    return api.update({ type: PATH, id }, {}, payload);
  },

  /**
   * Resend quotations for all suppliers with force create
   * @param {string} id - The ID of the request for quotation
   * @param {string} message - The new message to send
   * @returns {Promise<Object>} The API response
   */
  resend: async (id, message) => api.create(
    { type: PATH, id, action: "resend" },
    {},
    { message }
  ),

  /**
   * SELLWISOR-93: mark a supplier as excluded — it stays on the request
   * (status "excluded") but never receives quotations
   * @param {string} rfqId - The ID of the request for quotation
   * @param {string|string[]} supplierId - The ID(s) of the supplier(s) to exclude
   * @returns {Promise<Object>} The API response
   */
  excludeSupplier: (rfqId, supplierId) => api.create(
    { type: PATH, id: rfqId, action: "exclude_supplier" },
    {},
    { supplier_id: supplierId }
  ),

  /**
   * FIN-1102 / SELLWISOR-93: per-supplier quoting behaviour for the request —
   * average quote latency and which RLs the supplier keeps declining
   * @param {string} id - The ID of the request for quotation
   * @returns {Promise<Object>} The API response ({ data: { [supplierId]: stats } })
   */
  supplierStats: (id) => api.read(
    { type: PATH, id, action: "supplier_stats" },
    {}
  )
};

export default requestForQuotations;
